import type { FunctionComponent, ReactNode } from 'react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import cn from 'classnames';
import { throttle } from 'lodash';

import ArrowIcon from './ArrowIcon';
import styles from './styles.css';

export interface AccordionItemProps {
    title: string;
    description: ReactNode;
}

const AccordionItem: FunctionComponent<AccordionItemProps> = ({ title, description }) => {
    const [opened, setOpened] = useState<boolean>(false);
    const descriptionRef = useRef<HTMLDivElement>(null);
    const [descriptionStyle, setDescriptionStyle] = useState<{ height?: string }>({});

    const toggleOpen = useCallback(() => {
        setOpened((value) => !value);
    }, []);

    const updateDescriptionStyle = useCallback(() => {
        setDescriptionStyle(
            opened
                ? {
                      height: `${descriptionRef.current?.getBoundingClientRect().height || 0}px`,
                  }
                : {
                      height: '0px',
                  }
        );
    }, [opened, setDescriptionStyle, descriptionRef]);

    useEffect(() => {
        updateDescriptionStyle();
    }, [updateDescriptionStyle]);

    const throttledResizeHandler = useMemo(() => throttle(updateDescriptionStyle, 100), [updateDescriptionStyle]);

    useEffect(() => {
        window.addEventListener('resize', throttledResizeHandler);

        return () => {
            window.removeEventListener('resize', throttledResizeHandler);
        };
    }, [throttledResizeHandler]);

    return (
        <div onClick={toggleOpen} className={styles['AccordionItem-Root']}>
            <div className={styles['AccordionItem-TitleAndButtonWrapper']}>
                <div className={styles['AccordionItem-Title']}>{title}</div>
                <button className={styles['AccordionItem-ShowButton']}>
                    <ArrowIcon className={cn(styles['AccordionItem-ButtonIcon'], { [styles.opened]: opened })} />
                </button>
            </div>
            <div className={styles['AccordionItem-DescriptionWrapper']} style={descriptionStyle}>
                <div
                    ref={descriptionRef}
                    className={cn(styles['AccordionItem-Description'], { [styles.opened]: opened })}
                >
                    {description}
                </div>
            </div>
        </div>
    );
};

export default AccordionItem;
